/**
 * QuotaUsageCard — V4 tier quota display with progress bars for each
 * capability cap (Premium AI saves, Ask BrainSync, video minutes, storage).
 *
 * Uses `/api/subscriptions/status` fields:
 *   • premium_used / premium_limit
 *   • ask_used / ask_limit
 *   • video_minutes_used / video_minutes_limit
 *   • storage_used_mb / storage_max_mb
 */
import React from 'react';
import { Sparkles, MessageCircle, Clock, HardDrive } from 'lucide-react';
import { Button } from './ui/button';

const TIER_LABEL = {
    free: 'Basic (Free)',
    pro_200: 'Plus',
    pro_unlimited: 'Pro',
};

function fmtMB(mb) {
    if (mb == null) return '—';
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb >= 10) return `${Math.round(mb)} MB`;
    return `${mb.toFixed(1)} MB`;
}

function QuotaBar({ label, used, limit, unit, icon: Icon, unlimited, testid }) {
    const pct = unlimited ? 0 : Math.min(100, Math.round(((used || 0) / (limit || 1)) * 100));
    const isNearLimit = pct >= 80;
    const isFull = pct >= 100;
    const barColor = isFull ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-emerald-500';
    return (
        <div className="space-y-1.5" data-testid={testid}>
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon className="w-3.5 h-3.5" />
                    <span className="font-medium">{label}</span>
                </div>
                <span className={`font-mono text-[11px] ${isFull ? 'text-red-600 font-semibold' : isNearLimit ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                    {unlimited ? 'Unlimited' : `${used || 0} / ${limit} ${unit || ''}`.trim()}
                </span>
            </div>
            {!unlimited && (
                <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                        className={`h-full ${barColor} transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </div>
    );
}

export default function QuotaUsageCard({ data, tier, onUpgrade }) {
    if (!data) return null;

    const {
        premium_used = 0,
        premium_limit = 20,
        ask_used = 0,
        ask_limit = 10,
        video_minutes_used = 0,
        video_minutes_limit = 50,
        storage_used_mb = 0,
        storage_max_mb = 100,
    } = data;

    const isFree = (tier || 'free') === 'free';
    const isPro = (tier || 'free') === 'pro_unlimited';
    const tierLabel = TIER_LABEL[tier] || TIER_LABEL.free;

    // Free saves are LIFETIME — label accordingly
    const savesLabel = isFree ? 'Premium AI saves (lifetime)' : 'Premium AI saves / mo';

    // Any quota at 80%+ triggers an upgrade nudge for non-Pro tiers
    const anyNearLimit = !isPro && (
        (premium_limit > 0 && premium_used / premium_limit >= 0.8) ||
        (ask_limit > 0 && ask_used / ask_limit >= 0.8) ||
        (video_minutes_limit > 0 && video_minutes_used / video_minutes_limit >= 0.8) ||
        (storage_max_mb > 0 && storage_used_mb / storage_max_mb >= 0.8)
    );

    return (
        <section
            className="rounded-2xl border border-border bg-card p-5 space-y-4"
            data-testid="quota-usage-card"
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">Usage this period</p>
                    <p className="text-sm text-foreground mt-0.5">
                        You&apos;re on <span className="font-semibold">{tierLabel}</span>
                    </p>
                </div>
                {anyNearLimit && onUpgrade && (
                    <Button
                        size="sm"
                        onClick={onUpgrade}
                        data-testid="quota-upgrade-cta"
                    >
                        Upgrade
                    </Button>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                <QuotaBar
                    label={savesLabel}
                    used={premium_used}
                    limit={premium_limit}
                    icon={Sparkles}
                    unlimited={premium_limit === -1}
                    testid="quota-bar-saves"
                />
                <QuotaBar
                    label="Ask BrainSync"
                    used={ask_used}
                    limit={ask_limit}
                    icon={MessageCircle}
                    unlimited={ask_limit === -1}
                    testid="quota-bar-ask"
                />
                <QuotaBar
                    label="Video minutes"
                    used={video_minutes_used}
                    limit={video_minutes_limit}
                    unit="min"
                    icon={Clock}
                    unlimited={video_minutes_limit === -1}
                    testid="quota-bar-video-min"
                />
                <div className="space-y-1.5" data-testid="quota-bar-storage">
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <HardDrive className="w-3.5 h-3.5" />
                            <span className="font-medium">Attachment storage</span>
                        </div>
                        <span className={`font-mono text-[11px] ${(storage_used_mb / storage_max_mb) >= 1 ? 'text-red-600 font-semibold' : (storage_used_mb / storage_max_mb) >= 0.8 ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                            {fmtMB(storage_used_mb)} / {fmtMB(storage_max_mb)}
                        </span>
                    </div>
                    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                            className={`h-full transition-all duration-500 ${
                                (storage_used_mb / storage_max_mb) >= 1 ? 'bg-red-500' :
                                (storage_used_mb / storage_max_mb) >= 0.8 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.round((storage_used_mb / storage_max_mb) * 100))}%` }}
                        />
                    </div>
                </div>
            </div>

            {isFree && (
                <p className="text-[11px] text-muted-foreground pt-1">
                    Premium AI saves on Basic are lifetime (not monthly). Ask BrainSync resets every 30 days.
                </p>
            )}
        </section>
    );
}
